import { Link, useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, Check, Clock, Loader2 } from 'lucide-react';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { formatInstant } from '@/lib/date';

/**
 * Per-message send state for one batch (PRD §F3).
 *
 * The screen exists because "sent 10 inquiries" is not a truthful summary while
 * the sends are staggered across several minutes — and because a partial
 * failure needs somewhere to be visible and retryable, rather than being
 * flattened into a success toast.
 */
export default function BatchProgressPage() {
  const { batchId } = useParams<{ id: string; batchId: string }>();
  const queryClient = useQueryClient();

  const { data: batch } = useQuery({
    queryKey: ['batch', batchId],
    queryFn: async () => {
      const res = await client.api.inquiries.batch[':batchId'].$get({
        param: { batchId: batchId! },
      });
      if (!res.ok) throw new Error('Failed to load batch');
      return res.json();
    },
    enabled: !!batchId,
    // Poll while anything is still in flight; stop once the batch settles so an
    // open tab does not hammer the API for hours.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 5000;
      const pending = data.inquiries.some(
        (i) => i.sendState === 'queued' || i.sendState === 'sending',
      );
      return pending ? 5000 : false;
    },
  });

  const retry = useMutation({
    mutationFn: async () => {
      const res = await client.api.inquiries.batch[':batchId'].retry.$post({
        param: { batchId: batchId! },
      });
      if (!res.ok) throw new Error('Retry failed');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['batch', batchId] });
      toast.success(`${data.requeued} re-queued`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (!batch) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const sent = batch.inquiries.filter((i) => i.sendState === 'sent').length;
  const failed = batch.inquiries.filter((i) => i.sendState === 'failed').length;
  const pending = batch.inquiries.length - sent - failed;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Link
          to={`/weddings/${batch.weddingId}`}
          className="label-caps text-xs text-muted-foreground hover:text-foreground"
        >
          ← Back to the wedding
        </Link>
        <h1 className="display-tight text-3xl font-semibold">
          {batch.category} inquiries
        </h1>
        <p className="text-sm text-muted-foreground">
          {sent} sent
          {pending > 0 && ` · ${pending} still queued`}
          {failed > 0 && ` · ${failed} failed`}
        </p>
      </div>

      {pending > 0 && (
        <p className="rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-3 text-sm text-muted-foreground">
          Messages go out 20–60 seconds apart so they don't look like a mail merge to a spam
          filter. You can close this page — sending continues.
        </p>
      )}

      {failed > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">
            {failed} {failed === 1 ? 'message' : 'messages'} didn't send. The rest went out fine.
          </p>
          <Button size="sm" disabled={retry.isPending} onClick={() => retry.mutate()}>
            {retry.isPending ? 'Retrying…' : `Retry ${failed}`}
          </Button>
        </div>
      )}

      <Card className="rounded-3xl bg-card p-5 shadow-ambient">
        <div className="divide-y divide-border">
          {batch.inquiries.map((inq) => (
            <Link
              key={inq.id}
              to={`/inquiries/${inq.id}`}
              className="flex items-center justify-between gap-3 py-3 transition-colors hover:bg-[var(--color-surface-container-high)]"
            >
              <div className="flex min-w-0 items-center gap-3">
                <SendIcon state={inq.sendState} />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{inq.vendorName}</p>
                  {inq.sendError && (
                    <p className="truncate text-xs text-destructive">{inq.sendError}</p>
                  )}
                </div>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {inq.sentAt
                  ? `Sent ${formatInstant(inq.sentAt, { hour: 'numeric', minute: '2-digit' })}`
                  : inq.scheduledSendAt
                    ? `Due ${formatInstant(inq.scheduledSendAt, { hour: 'numeric', minute: '2-digit' })}`
                    : ''}
              </span>
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}

function SendIcon({ state }: { state: string }) {
  if (state === 'sent') return <Check className="size-4 shrink-0 text-primary" />;
  if (state === 'failed') return <AlertCircle className="size-4 shrink-0 text-destructive" />;
  if (state === 'sending') return <Loader2 className="size-4 shrink-0 animate-spin" />;
  return <Clock className="size-4 shrink-0 text-muted-foreground" />;
}
